/**
 * AbandonmentFeedback — Phase Q4 abandonment feedback + suppression UI.
 *
 * Placed BELOW the primary "Complete My Order" CTA on both the fresh
 * quote (Step 3 Review) and the resume flow. Starts as a collapsed
 * secondary link so it never visually competes with checkout.
 *
 * Authorization is passed through by the parent:
 *   • `resumeToken` (Q2 magic-link customer) — recommended when
 *     rendering under `?resume=2`.
 *   • `requestId` + `sessionId` (current-browser Q1 customer) — the
 *     same pair used for the Q1 idempotent upsert.
 *
 * On success:
 *   • Generic thank-you.
 *   • `have_question` → prominent WhatsApp + Instagram contact CTAs.
 *   • `quote_problem` → thank-you framing acknowledging the report.
 * The component NEVER hides the parent's Complete My Order button.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { MessageSquare, Send, MessageCircle, Instagram, CheckCircle2, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = [
  { code: "price_too_high",   label: "The price was higher than I expected", icon: "💰" },
  { code: "just_checking",    label: "I was just checking the price",         icon: "👀" },
  { code: "found_elsewhere",  label: "I already found the stickers somewhere else", icon: "✅" },
  { code: "no_longer_needed", label: "I no longer need these stickers",       icon: "❌" },
  { code: "ordering_later",   label: "I'm planning to order later",           icon: "⏰" },
  { code: "shipping_cost",    label: "Shipping cost",                          icon: "📦" },
  { code: "payment_method",   label: "I don't want to use the available payment method", icon: "💳" },
  { code: "have_question",    label: "I have a question before ordering",     icon: "❓" },
  { code: "quote_problem",    label: "Something is wrong with my quote",      icon: "⚠️" },
  { code: "other",            label: "Other",                                  icon: "✏️" },
];

const REASONS_REQUIRING_COMMENT_HINT = new Set(["quote_problem", "other"]);

export default function AbandonmentFeedback({
  publicId,
  resumeToken,     // Q2 magic-link authorization
  requestId,       // Q1 current-browser authorization pair
  sessionId,
  source,          // "website_quote" | "resume_link"
  existingFeedback, // optional prior feedback if already submitted
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState(existingFeedback?.reason || "");
  const [comment, setComment] = useState(existingFeedback?.comment || "");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(Boolean(existingFeedback));
  const [contact, setContact] = useState({ whatsappHref: null, instagramHref: null });

  useEffect(() => {
    // Only fetch when the customer picks "have a question" or has
    // already done so — avoids an unnecessary settings call.
    if (reason !== "have_question" && !(submitted && existingFeedback?.reason === "have_question")) return;
    let alive = true;
    axios.get(`${API}/settings/public`).then((r) => {
      if (!alive) return;
      const wa = r.data?.whatsapp_url
        || (r.data?.whatsapp_business_number
              ? `https://wa.me/${String(r.data.whatsapp_business_number).replace(/\D/g, "")}`
              : null);
      const ig = r.data?.instagram_url || null;
      setContact({ whatsappHref: wa, instagramHref: ig });
    }).catch(() => {});
    return () => { alive = false; };
  }, [reason, submitted, existingFeedback]);

  const submit = async () => {
    if (!reason) { toast.error("Please choose a reason."); return; }
    setBusy(true);
    try {
      const payload = {
        reason,
        comment: (comment || "").trim() || null,
        source: source || "website_quote",
      };
      if (resumeToken) payload.t = resumeToken;
      else if (requestId && sessionId) {
        payload.request_id = requestId;
        payload.session_id = sessionId;
      }
      await axios.post(
        `${API}/orders/quote/${encodeURIComponent(publicId)}/feedback`,
        payload
      );
      setSubmitted(true);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not send feedback.";
      toast.error(String(detail));
    } finally {
      setBusy(false);
    }
  };

  if (!publicId) return null;
  if (!resumeToken && !(requestId && sessionId)) return null;   // no auth path

  const showQuestionHelp = submitted &&
    (reason === "have_question" || existingFeedback?.reason === "have_question");

  // ── Submitted state ──────────────────────────────────────────
  if (submitted) {
    return (
      <div
        className="mt-6 rounded-2xl border-2 border-emerald-800 bg-emerald-50 p-4"
        data-testid="abandonment-feedback-thankyou"
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 className="text-emerald-800 shrink-0 mt-0.5" size={18} />
          <div className="flex-1">
            <div className="font-semibold text-sm">Thanks for the feedback.</div>
            {showQuestionHelp ? (
              <>
                <p className="mt-1 text-sm">
                  <strong>We&#39;re happy to help.</strong> Message us and we&#39;ll get back to you fast.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {contact.whatsappHref && (
                    <a
                      href={contact.whatsappHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--ink)] bg-emerald-500 text-white px-3 py-1.5 text-sm font-bold no-underline hover:brightness-110"
                      data-testid="abandonment-feedback-whatsapp"
                    >
                      <MessageCircle size={14} /> Message us on WhatsApp
                    </a>
                  )}
                  {contact.instagramHref && (
                    <a
                      href={contact.instagramHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--ink)] bg-[var(--panini-red)] text-white px-3 py-1.5 text-sm font-bold no-underline hover:brightness-110"
                      data-testid="abandonment-feedback-instagram"
                    >
                      <Instagram size={14} /> DM us on Instagram
                    </a>
                  )}
                </div>
              </>
            ) : (
              <p className="mt-1 text-sm text-[var(--smoke)]">
                Your feedback helps us improve CollectorUp. You can still complete your order below anytime.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Collapsed entry point ────────────────────────────────────
  if (!open) {
    return (
      <div className="mt-6 flex justify-center">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-sm text-[var(--smoke)] hover:text-[var(--panini-red)] underline underline-offset-4 decoration-dotted transition-colors inline-flex items-center gap-1.5"
          data-testid="abandonment-feedback-open"
        >
          <MessageSquare size={14} /> Not ready to order? Tell us why <ChevronDown size={14} />
        </button>
      </div>
    );
  }

  const commentHint = REASONS_REQUIRING_COMMENT_HINT.has(reason);

  // ── Expanded form ────────────────────────────────────────────
  return (
    <div
      className="mt-6 rounded-2xl border-2 border-[var(--ink)] bg-white p-4 md:p-5"
      data-testid="abandonment-feedback-form"
    >
      <div className="flex items-center justify-between gap-3 mb-1">
        <div>
          <h3 className="font-display text-lg">Not ready to order?</h3>
          <p className="text-xs text-[var(--smoke)] mt-0.5">
            We&#39;d love to know why. Your feedback helps us improve CollectorUp.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-xs text-[var(--smoke)] hover:text-[var(--ink)] inline-flex items-center gap-1 shrink-0"
          data-testid="abandonment-feedback-close"
        >
          <ChevronUp size={14} /> Hide
        </button>
      </div>

      <div className="mt-3">
        <div className="text-[11px] uppercase tracking-widest font-bold text-[var(--ink)] mb-2">
          What is stopping you from completing your order?
        </div>
        <div className="grid gap-1.5">
          {REASONS.map((r) => (
            <label
              key={r.code}
              className={`flex items-start gap-2 rounded-lg border-2 p-2 cursor-pointer transition-colors ${
                reason === r.code
                  ? "border-[var(--panini-red)] bg-[var(--panini-yellow-soft)]"
                  : "border-neutral-200 hover:border-[var(--ink)]"
              }`}
              data-testid={`abandonment-feedback-reason-${r.code}`}
            >
              <input
                type="radio"
                name="q4-feedback-reason"
                value={r.code}
                checked={reason === r.code}
                onChange={() => setReason(r.code)}
                className="mt-1 accent-[var(--panini-red)]"
              />
              <span className="text-sm">
                <span className="mr-1" aria-hidden="true">{r.icon}</span>
                {r.label}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="mt-4">
        <label className="block">
          <span className="text-[11px] uppercase tracking-widest font-bold text-[var(--ink)]">
            {reason === "quote_problem"
              ? "Tell us what looks wrong so we can check it."
              : "Anything else you'd like us to know?"}
          </span>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={2000}
            rows={3}
            placeholder={
              commentHint
                ? "Please share a few details so we can help."
                : "Optional"
            }
            className="mt-1 w-full border-2 border-[var(--ink)] rounded-xl p-2 text-sm focus:outline-none focus:ring-4 focus:ring-[var(--panini-yellow)]"
            data-testid="abandonment-feedback-comment"
          />
        </label>
      </div>

      <div className="mt-4 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-sm text-[var(--smoke)] hover:text-[var(--ink)]"
          data-testid="abandonment-feedback-cancel"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={busy || !reason}
          className="inline-flex items-center gap-1.5 rounded-full border-2 border-[var(--ink)] bg-[var(--ink)] text-white px-4 py-2 text-sm font-bold disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="abandonment-feedback-submit"
        >
          <Send size={14} /> {busy ? "Sending…" : "Send feedback"}
        </button>
      </div>
    </div>
  );
}
